import { useState } from "react";
import styled, { css } from "styled-components";
import { COLOR_BLACK, COLOR_GRAY, COLOR_RED, COLOR_WHITE } from "../constants/colors";
import { Divider } from "../pages/Payment/shared";
import Button from "./Button";
import Spinner from "./Spinner";
import { Heading4, Heading5, Heading6, Subtitle1 } from "./Typography";

type SizeProps = "sm" | "md";

export type OrderProps = {
  label: string;
  qty?: number;
  price?: number;
  totalPrice: number;
};

const Container = styled.div<{ size: SizeProps }>`
  width: 100%;
  background-color: ${COLOR_WHITE};
  border-radius: 8px;
  margin-bottom: 40px;

  ${(props) =>
    props.size === "md" &&
    css`
      padding: 20px 36px;
    `}

  ${(props) =>
    props.size === "sm" &&
    css`
      padding: 20px;
    `}

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Title = styled.div`
  ${Heading4}
  color: ${COLOR_BLACK};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Order = styled.div<{ size: SizeProps }>`
  color: ${COLOR_BLACK};
  ${(props) =>
    props.size === "md" &&
    css`
      ${Heading6}
      line-height: 31px;
    `}

  ${(props) =>
    props.size === "sm" &&
    css`
      ${Subtitle1}
    `}
`;

const TotalPrice = styled.div<{ size: SizeProps }>`
  color: ${COLOR_BLACK};
  ${(props) =>
    props.size === "md" &&
    css`
      ${Heading4}
    `}

  ${(props) =>
    props.size === "sm" &&
    css`
      ${Heading5}
    `}
`;

const PaymentButton = styled(Button)`
  min-width: 150px;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PaymentButtonsContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ErrorMessage = styled.p`
  color: ${COLOR_RED};
  margin-bottom: 0;
`;

type OrderSummaryProps = {
  size: SizeProps;
  title: string;
  orders: OrderProps[];
  handlePlaceOrder: () => void;
  totalPrice: number;
  isLoading?: boolean;
  isDisabled?: boolean;
  error?: string;
  applyCoupon?: Function;
};

function OrderSummary({
  title,
  size,
  orders,
  handlePlaceOrder,
  totalPrice,
  isDisabled,
  isLoading,
  error,
  applyCoupon,
}: OrderSummaryProps) {
  const [coupon, setCoupon] = useState("");

  const handleCouponChanged = (e: any) => {
    setCoupon(e.target.value);
  };

  return (
    <Container size={size}>
      <Title className="mb-3">{title}</Title>
      {orders.map((order) => (
        <Row key={order.label} className="mb-3 mt-1">
          <Order size={size} className="me-2 me-md-4">
            {order.label}
            {order.qty && (
              <>
                <br />
                <small style={{ color: COLOR_GRAY }}>Qty {order.qty}</small>
              </>
            )}
          </Order>
          <Order size={size} className="mb-auto" style={{ textAlign: "right" }}>
            ${order.totalPrice.toFixed(2)}
            {order.price && (
              <>
                <br />
                <small style={{ color: COLOR_GRAY }}>${order.price.toFixed(2)} each</small>
              </>
            )}
          </Order>
        </Row>
      ))}
      {applyCoupon && (
        <Row>
          <input
            type="text"
            className="form-control rounded-pill rounded-end text-uppercase"
            placeholder="Add promotion code"
            onChange={handleCouponChanged}
            value={coupon}
          />
          <button
            type="submit"
            onClick={() => applyCoupon(coupon)}
            className="btn btn-dark rounded-pill rounded-start px-4"
          >
            Apply
          </button>
        </Row>
      )}
      <Row className="mt-3">
        <TotalPrice size={size}>Total Price</TotalPrice>
        <TotalPrice size={size}>${totalPrice.toFixed(2)}</TotalPrice>
      </Row>
      <Divider />
      <PaymentButtonsContainer>
        <PaymentButton variant="secondary" size="sm" onClick={handlePlaceOrder} disabled={isDisabled || isLoading}>
          {isLoading ? <Spinner size="33px" borderWidth="0.25rem" animation="border" /> : "Place Order"}
        </PaymentButton>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </PaymentButtonsContainer>
    </Container>
  );
}

export default OrderSummary;

OrderSummary.defaultProps = {
  isLoading: false,
  isDisabled: false,
};
